<script setup lang="ts">
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'

import RescheduleCalculationDialog from '@/components/calculation/RescheduleCalculationDialog.vue'
import StopCalculationDialog from '@/components/calculation/StopCalculationDialog.vue'
import { useCalculationPermissions } from '@/composables/useCalculationPermissions'
import router from '@/router'
import { useCalculationStore } from '@/stores/calculation'
import type { Calculation, CalculationReschedulePayload } from '@/stores/type/calculation.type'

const props = defineProps<{
  item: Calculation
  spendingCapExceeded?: boolean
  spendingLoaded?: boolean
  spendingLoading?: boolean
}>()

const calculationStore = useCalculationStore()
const { rescheduleLoading, stopLoading } = storeToRefs(calculationStore)
const { canAddCalculation, canStopCalculation, canDeleteCalculation } = useCalculationPermissions()
const rescheduleDialog = ref(false)
const stopDialog = ref(false)
const continuingDraft = ref(false)

const isRescheduling = computed(() => props.item.id === rescheduleLoading.value)
const isStopping = computed(() => props.item.id === stopLoading.value)
const isRunning = computed(() => ['RUNNING', 'UPLOADING'].includes(props.item.status))
const isDraft = computed(() => props.item.status === 'DRAFT' && !props.item.task_id)
const canReschedule = computed(() =>
  ['ERROR', 'UPLOAD_FAILED', 'TIMEOUT', 'CANCELLED'].includes(String(props.item.status ?? ''))
)

const rescheduleDisabledReason = computed(() => {
  if (!canReschedule.value) return ''
  if (props.spendingLoading || !props.spendingLoaded) return 'Checking project spending caps.'
  if (props.spendingCapExceeded) {
    return 'Project spending caps are exceeded. Increase the cap before rescheduling.'
  }
  return ''
})

const rescheduleDisabled = computed(
  () => Boolean(rescheduleDisabledReason.value) || isRescheduling.value
)

const handleDeleteCalculation = () => {
  calculationStore.setCurrentItem(props.item)
  calculationStore.setDeleteItem(props.item)
  calculationStore.openDeleteDialog()
}

const continueDraft = async () => {
  if (continuingDraft.value) return
  continuingDraft.value = true
  try {
    calculationStore.setCurrentItem(props.item)
    if (!props.item.input_ready || props.item.input_auto_start) {
      calculationStore.openCreateUpdateDialog()
      return
    }
    const calculation = await calculationStore.celeryCalculate(props.item)
    if (calculation)
      await router.push({
        name: 'calculation-result',
        params: { id: calculation.project, calculationId: calculation.id }
      })
  } finally {
    continuingDraft.value = false
  }
}

const handleRescheduleCalculation = async (payload: CalculationReschedulePayload) => {
  if (rescheduleDisabled.value) return
  const calculation = await calculationStore.rescheduleCalculation(props.item, payload)
  if (!calculation) return
  rescheduleDialog.value = false
  await router.push({
    name: 'calculation-result',
    params: { id: calculation.project, calculationId: calculation.id }
  })
}

const handleStopCalculation = async () => {
  if (!isRunning.value || isStopping.value) return
  const calculation = await calculationStore.stopCalculation(props.item)
  if (!calculation) return
  stopDialog.value = false
}
</script>

<template>
  <div class="calculation-actions" @click.stop>
    <div class="calculation-action-slot">
      <template v-if="canAddCalculation">
        <v-btn
          v-if="isDraft"
          :loading="continuingDraft"
          :icon="item.input_ready ? 'mdi-play-outline' : 'mdi-upload-outline'"
          :title="item.input_ready ? 'Start calculation' : 'Complete input upload'"
          variant="text"
          density="comfortable"
          @click="continueDraft"
        />
        <v-tooltip
          v-if="canReschedule"
          :disabled="!rescheduleDisabledReason"
          :text="rescheduleDisabledReason"
          location="top"
        >
          <template #activator="{ props: tooltipProps }">
            <span v-bind="tooltipProps">
              <v-btn
                icon="mdi-restart"
                variant="text"
                density="comfortable"
                :disabled="rescheduleDisabled"
                :loading="isRescheduling"
                @click="rescheduleDialog = true"
              />
            </span>
          </template>
        </v-tooltip>
      </template>

      <v-btn
        v-if="canStopCalculation && isRunning"
        icon="mdi-stop-circle-outline"
        variant="text"
        density="comfortable"
        color="error"
        :loading="isStopping"
        :disabled="isStopping"
        @click="stopDialog = true"
      />
    </div>

    <div class="calculation-action-slot">
      <v-btn
        v-if="canDeleteCalculation"
        density="comfortable"
        icon="mdi-trash-can-outline"
        variant="text"
        @click="handleDeleteCalculation"
      />
    </div>
  </div>

  <RescheduleCalculationDialog
    v-model="rescheduleDialog"
    :calculation-name="item.name"
    :project-id="item.project"
    :hardware-profile-id="item.hardware_profile"
    :container-image="item.container_image"
    :vm-max-runtime-seconds="item.vm_max_runtime_seconds"
    :loading="isRescheduling"
    @confirm="handleRescheduleCalculation"
  />

  <StopCalculationDialog
    v-model="stopDialog"
    :calculation-name="item.name"
    :loading="isStopping"
    @confirm="handleStopCalculation"
  />
</template>

<style scoped>
.calculation-actions {
  align-items: center;
  display: grid;
  grid-template-columns: repeat(2, 40px);
  justify-content: center;
  white-space: nowrap;
}

.calculation-action-slot {
  align-items: center;
  display: flex;
  justify-content: center;
  min-width: 40px;
}
</style>
